// react
import { useContext, useMemo, useState } from 'react';

// mui
import { Box } from '@mui/material';
import {
	Add as IconAdd,
	Download as IconDownload,
	People as IconBeneficiary,
	UploadFile as IconUpload,
} from '@mui/icons-material';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBeneficiaries, ContextLoans, ContextProject } from '../../Project/Project.context';

// hooks
import { useProjectLookups } from '../../../../hooks/useProjectLookups';

// router
import { useSearchParams } from 'react-router-dom';

// ui
import { CustomFieldsLink } from '../../../ui/CustomFieldsLink/CustomFieldsLink';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';

// dialogs
import { DialogAddBeneficiary } from '../../../dialogs/DialogAddBeneficiary/DialogAddBeneficiary';
import { DialogEditBeneficiary } from '../../../dialogs/DialogEditBeneficiary/DialogEditBeneficiary';
import { DialogUpdateEntityStatus } from '../../../dialogs/DialogUpdateEntityStatus/DialogUpdateEntityStatus';
import { DialogUploadDocsEntity } from '../../../dialogs/DialogUploadDocsEntity/DialogUploadDocsEntity';
import {
	DialogBulkUpload,
	v1BeneficiarySchema,
	v2VoucherSchema,
	downloadTemplate,
} from '../../../dialogs/DialogBulkUpload';
import type { UploadSchema } from '../../../dialogs/DialogBulkUpload';

// components
import { BeneficiariesDataTable } from './BeneficiariesDataTable';

// types
import { Entity } from '../../../../@types';
import { voucherStatuses } from '../../../../@types/unions';

export const Beneficiaries = () => {
	// hooks
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// state for deactivate dialog
	const [deactivateEntity, setDeactivateEntity] = useState<Entity | null>(null);
	const [uploadDocsEntity, setUploadDocsEntity] = useState<Entity | null>(null);

	// context
	const { beneficiaries, isLoadingBeneficiaries, paginationBeneficiaries, refetchBeneficiaries } =
		useContext(ContextBeneficiaries);
	const { loans } = useContext(ContextLoans);
	const { project } = useContext(ContextProject);

	// Full project-universe lookup lists for bulk-upload relation resolution.
	// Recipients are the project's beneficiaries; accounts are the
	// standalone/direct funding outlet and approved spend plans are the
	// earmarked-payment outlet. Loans come from context (fully loaded there).
	const bulkUploadOpen =
		searchParams.get('dialog') === 'bulkUpload' ||
		searchParams.get('dialog') === 'uploadTransactions';
	const lookups = useProjectLookups({
		projectId: project?.id,
		isEnabled: bulkUploadOpen,
		entityTypes: 'INTERMEDIARY,PIU,SUB_PIU',
		accounts: true,
		spendPlans: true,
	});

	// Lookup data for bulk upload relation resolution
	const lookupData = useMemo(
		() => ({
			entities: lookups.entities,
			loans: loans.map((l) => ({
				id: l.id,
				name: l.name || l.code || String(l.id),
				code: l.code,
			})),
			accounts: lookups.accounts,
			spendPlans: lookups.spendPlans,
		}),
		[lookups.entities, lookups.accounts, lookups.spendPlans, loans]
	);

	// Beneficiary-scoped variant of the voucher upload schema. A beneficiary
	// transaction is always a payment to a beneficiary, so default the recipient
	// type to BENEFICIARY and require the beneficiary reference.
	const beneficiaryVoucherSchema = useMemo<UploadSchema>(
		() => ({
			...v2VoucherSchema,
			label: 'Beneficiary Transactions',
			fields: [
				...v2VoucherSchema.fields.map((field) => {
					if (field.name === 'recipientType') {
						return { ...field, defaultValue: 'BENEFICIARY' };
					}
					if (field.name === 'recipientEntityId') {
						return { ...field, required: true, label: 'Beneficiary' };
					}
					return field;
				}),
				// Uploaded transactions enter the payment queue directly in the
				// "Awaiting Review" bucket (VERIFIED), mirroring the supplier
				// prepare-for-payment submit. Left blank, the backend would default
				// the voucher to GENERATED, which the Payments page hides as a draft.
				{
					name: 'status',
					label: 'Status',
					dataType: 'enum' as const,
					required: false,
					enumValues: voucherStatuses,
					defaultValue: 'VERIFIED',
				},
			],
		}),
		[]
	);

	// params
	const paramDialog = searchParams.get('dialog');
	const searchQuery = searchParams.get('search') || searchParams.get('q') || '';
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '10';
	const filterOrderBy = searchParams.get('orderBy') || 'created';
	const filterOrder = (searchParams.get('order') || 'DESC') as 'ASC' | 'DESC';
	const filterSubtype = searchParams.get('subtype') || 'ALL';

	// Custom field definitions for beneficiaries
	const customFieldDefs = useMemo(
		() =>
			(project?.customFieldDefinitions || []).find((d) => d.entityType === 'beneficiary')
				?.definitions || [],
		[project]
	);

	// ==========================================================================
	// URL param handlers (Contracts pattern)
	// ==========================================================================

	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
				params.set('q', value);
			} else {
				params.delete('search');
				params.delete('q');
			}
			params.set('page', '1');
			return params;
		});
	};

	const handleFilterChange = (key: string, value: string | number) => {
		setSearchParams((params) => {
			if (value === 'ALL') {
				params.delete(key);
			} else {
				params.set(key, String(value));
			}
			params.set('page', '1');
			return params;
		});
	};

	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set('page', String(page));
			return params;
		});
	};

	const handleRowsPerPageChange = (rowsPerPage: number) => {
		setSearchParams((params) => {
			params.set('limit', String(rowsPerPage));
			params.set('page', '1');
			return params;
		});
	};

	const handleSortChange = (column: string, direction: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', column);
			params.set('order', direction);
			return params;
		});
	};

	const handleEditBeneficiary = (entity: { id: number }) => {
		setSearchParams((params) => {
			params.set('dialog', 'editBeneficiary');
			params.set('dialogBeneficiary', String(entity.id));
			return params;
		});
	};

	const handleDeactivateBeneficiary = (entity: Entity) => {
		setDeactivateEntity(entity);
	};

	const handleUploadDocuments = (entity: Entity) => {
		setUploadDocsEntity(entity);
	};

	return (
		<>
			<HeaderListingPage
				icon={IconBeneficiary}
				title={t('beneficiaries')}
				subtitle={t('spendPlan.beneficiaries.subtitle')}
				variant="contract"
				secondaryBadge={<CustomFieldsLink entity="beneficiary" />}
				actions={
					<BtnSplit
						actions={[
							{
								children: t('addBeneficiary'),
								onClick: () => {
									searchParams.set('dialog', 'addBeneficiary');
									setSearchParams(searchParams);
								},
								startIcon: <IconAdd />,
							},
							{
								children: t('beneficiaries.bulkUploadBeneficiaries', 'Bulk Upload Beneficiaries'),
								onClick: () => {
									searchParams.set('dialog', 'bulkUpload');
									setSearchParams(searchParams);
								},
								startIcon: <IconUpload />,
							},
							{
								children: t(
									'beneficiaries.downloadBeneficiaryTemplate',
									'Download Beneficiary Template'
								),
								onClick: async () => {
									try {
										await downloadTemplate({ schema: v1BeneficiarySchema });
									} catch {
										/* ignore */
									}
								},
								startIcon: <IconDownload />,
							},
							// TODO: re-enable once beneficiary transaction upload is fully wired up
							// {
							// 	children: t('beneficiaries.uploadTransactions', 'Upload Transactions'),
							// 	onClick: () => {
							// 		searchParams.set('dialog', 'uploadTransactions');
							// 		setSearchParams(searchParams);
							// 	},
							// 	startIcon: <IconUpload />,
							// },
							// {
							// 	children: t(
							// 		'beneficiaries.downloadTransactionTemplate',
							// 		'Download Transaction Template'
							// 	),
							// 	onClick: async () => {
							// 		try {
							// 			await downloadTemplate({ schema: beneficiaryVoucherSchema });
							// 		} catch {
							// 			/* ignore */
							// 		}
							// 	},
							// 	startIcon: <IconDownload />,
							// },
						]}
					/>
				}
			/>
			<Box sx={{ px: 3, pb: 3, pt: 2 }}>
				<BeneficiariesDataTable
					beneficiaries={beneficiaries}
					isLoading={isLoadingBeneficiaries}
					searchValue={searchQuery}
					onSearchChange={handleSearchChange}
					orderBy={filterOrderBy}
					order={filterOrder}
					onSortChange={handleSortChange}
					page={parseInt(filterPage)}
					rowsPerPage={parseInt(filterLimit)}
					totalCount={paginationBeneficiaries?.total || 0}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
					filterValues={{
						subtype: filterSubtype,
					}}
					onFilterChange={handleFilterChange}
					onEditBeneficiary={handleEditBeneficiary}
					onDeactivateBeneficiary={handleDeactivateBeneficiary}
					onUploadDocuments={handleUploadDocuments}
					customFieldDefinitions={customFieldDefs}
				/>
			</Box>
			<DialogAddBeneficiary
				isOpen={paramDialog === 'addBeneficiary'}
				onClose={() => {
					searchParams.delete('dialog');
					setSearchParams(searchParams);
				}}
				onSuccess={() => {
					refetchBeneficiaries();
				}}
			/>
			<DialogEditBeneficiary
				isOpen={paramDialog === 'editBeneficiary'}
				onClose={() => {
					setSearchParams((params) => {
						params.delete('dialog');
						params.delete('dialogBeneficiary');
						return params;
					});
				}}
				onSuccess={() => {
					refetchBeneficiaries();
				}}
			/>
			<DialogBulkUpload
				isOpen={paramDialog === 'bulkUpload'}
				onClose={() => {
					searchParams.delete('dialog');
					setSearchParams(searchParams);
				}}
				schema={v1BeneficiarySchema}
				projectId={project?.id || 0}
				lookupData={lookupData}
				onSuccess={() => {
					refetchBeneficiaries();
					void lookups.refetch(); // refresh lookups (e.g. entities created inline during resolution)
				}}
			/>
			<DialogBulkUpload
				isOpen={paramDialog === 'uploadTransactions'}
				onClose={() => {
					searchParams.delete('dialog');
					setSearchParams(searchParams);
				}}
				schema={beneficiaryVoucherSchema}
				projectId={project?.id || 0}
				lookupData={lookupData}
				onSuccess={() => {
					refetchBeneficiaries();
				}}
			/>
			{deactivateEntity && (
				<DialogUpdateEntityStatus
					entity={deactivateEntity}
					isOpen={!!deactivateEntity}
					onClose={() => setDeactivateEntity(null)}
					onSuccess={() => {
						setDeactivateEntity(null);
						refetchBeneficiaries();
					}}
				/>
			)}
			{uploadDocsEntity && (
				<DialogUploadDocsEntity
					entity={uploadDocsEntity}
					isOpen={!!uploadDocsEntity}
					onClose={() => setUploadDocsEntity(null)}
					onSuccess={() => {
						setUploadDocsEntity(null);
						refetchBeneficiaries();
					}}
				/>
			)}
		</>
	);
};
